import React, { useCallback, useEffect, useState } from "react";
import DataProvider from "../../../services";
import { useAppStore } from "../../../store";
import { useNavigate, useParams } from "react-router-dom";
import LoadWrapper from "../../../components/elements/load-wrapper";
import Empty from "../../../components/elements/empty";
import { Button, Image } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import MedicalRecordDetailModal from "../../../components/cards/medical-record-detail-modal";

const PAGE_SIZE = 10;

type RecordType = "prescription" | "appointment" | "diagnostics";

export default function MedicalRecords(props: any) {
  const { user } = useAppStore();
  const params = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null);

  const [data, setData] = useState<{
    medical_records: any[];
    totalPages: number;
  } | null>(null);

  // The backend tags each record with `_kind` specifically so the client
  // doesn't have to guess the type from field presence (which previously
  // misidentified diagnostics records and left every field showing "N/A").
  function getRecordType(record: any): RecordType {
    if (record._kind === "diagnostic") return "diagnostics";
    if (record._kind === "appointment") return "appointment";
    if (record._kind === "prescription") return "prescription";
    // Fallback for any older cached response without _kind.
    if (record.startTime) return "appointment";
    if (record.schedule_date || record.facilitation !== undefined) return "diagnostics";
    return "prescription";
  }

  const getRecords = useCallback(
    async (page = 1) => {
      try {
        setLoading(true);
        if (user) {
          const response = await DataProvider.getMedicalRecords(page, PAGE_SIZE);
          setData(response.data);
          setCurrentPage(page);
        }
      } catch {
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  useEffect(() => {
    if (params.id === "5" && user) {
      getRecords();
    }
  }, [params, getRecords, user]);

  function onCardClick(record: any) {
    const type = getRecordType(record);
    if (type === "appointment") {
      navigate(`/main/appointments/${record.id}`);
      return;
    }
    // Prescriptions and diagnostics don't have a dedicated detail page (only
    // appointments do), so their full details open in a modal instead.
    setSelectedRecord(record);
  }

  const totalPages = data?.totalPages ?? 0;

  return (
    <div className="bg-white w-full min-h-[65vh] rounded-lg border border-solid border-gray-200 py-4 md:px-4 px-2">
      {loading &&
      (!data?.medical_records || data.medical_records.length < 1) ? (
        <LoadWrapper loading={loading}>
          <div className="md:h-[85vh] w-full"></div>
        </LoadWrapper>
      ) : data?.medical_records && data.medical_records.length > 0 ? (
        // Keeps the current page's cards visible (dimmed, with a spinner
        // overlay) while a Previous/Next fetch is in flight, instead of the
        // page silently doing nothing until the response lands - so slow
        // networks/API lag are visible, not just "did my click register?".
        <LoadWrapper loading={loading}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {data.medical_records.map((record) => {
              const type = getRecordType(record);

              return (
                <div
                  key={`${getRecordType(record)}-${record.id}`}
                  onClick={() => onCardClick(record)}
                  className="border rounded-md p-4 shadow-sm bg-gray-50 transition cursor-pointer hover:bg-gray-100 hover:shadow-md"
                >
                  <div className="text-lg font-bold mb-2 capitalize">
                    {type} Report
                  </div>

                  {/* Appointment */}
                  {type === "appointment" && (
                    <>
                      <div>
                        <span className="font-bold"> Doctor:</span>{" "}
                        {record.doctorName?.trim() || "N/A"}
                      </div>
                      <div>
                        Patient Name: {record.patientName?.trim() || "N/A"}
                      </div>
                      <div>Date: {record.date?.trim() || "N/A"}</div>
                      <div className="text-gray-600">
                        Created At:{" "}
                        {record.createdAt
                          ? new Date(record.createdAt).toLocaleString()
                          : "N/A"}
                      </div>
                      <span className="text-xs text-blue-600">
                        Click to view appointment
                      </span>
                    </>
                  )}

                  {/* Diagnostics - real Diagnostic entity fields, not the
                      appointment-shaped ones this used to (wrongly) read */}
                  {type === "diagnostics" && (
                    <>
                      <div>
                        <span className="font-bold"> Patient:</span>{" "}
                        {record.fullname?.trim() || "N/A"}
                      </div>
                      <div>Test type: {record.type || "N/A"}</div>
                      <div>Provider: {record.provider || "N/A"}</div>
                      <div>
                        Date:{" "}
                        {record.schedule_date
                          ? new Date(record.schedule_date).toLocaleDateString()
                          : "N/A"}
                      </div>
                      <div className="text-gray-600">
                        Created At:{" "}
                        {record.createdAt
                          ? new Date(record.createdAt).toLocaleString()
                          : "N/A"}
                      </div>
                      <span className="text-xs text-blue-600">
                        Click to view details
                      </span>
                    </>
                  )}

                  {/* Prescription */}
                  {type === "prescription" && (
                    <>
                      <div>
                        <span className="font-bold"> Patient:</span>{" "}
                        {record.name?.trim() || "N/A"}
                      </div>
                      <div className="text-gray-600">
                        Created At:{" "}
                        {record.createdAt
                          ? new Date(record.createdAt).toLocaleString()
                          : "N/A"}
                      </div>
                      <div className="text-gray-600">
                        Order ID: {record.orderId || "N/A"}
                      </div>
                      {record.image && (
                        <div
                          className="mt-2"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Image
                            src={record.image}
                            alt="Prescription"
                            width={64}
                            height={64}
                            className="object-cover rounded"
                          />
                        </div>
                      )}
                      <span className="text-xs text-blue-600 block mt-1">
                        Click to view details
                      </span>
                    </>
                  )}
                </div>
              );
            })}
          </div>

          {totalPages > 1 && (
            <div className="mt-6 flex items-center justify-center gap-4">
              <Button
                icon={<LeftOutlined />}
                disabled={currentPage <= 1 || loading}
                loading={loading}
                onClick={() => getRecords(currentPage - 1)}
              >
                Previous
              </Button>
              <span className="text-sm text-gray-600">
                Viewing {currentPage} of {totalPages}
              </span>
              <Button
                disabled={currentPage >= totalPages || loading}
                loading={loading}
                onClick={() => getRecords(currentPage + 1)}
              >
                Next <RightOutlined />
              </Button>
            </div>
          )}
        </LoadWrapper>
      ) : (
        <div>
          <Empty
            imageStyle="md:h-44 h-24"
            titleStyle="md:text-lg text-sm"
            desStyle="md:text-base"
            title="No Medical Records"
            buttons={[
              {
                name: "Refresh",
                event: () => getRecords(),
              },
            ]}
            description="You have no medical records on our platform."
          />
        </div>
      )}

      <MedicalRecordDetailModal
        record={selectedRecord}
        type={
          selectedRecord
            ? (getRecordType(selectedRecord) as "prescription" | "diagnostics")
            : null
        }
        onClose={() => setSelectedRecord(null)}
      />
    </div>
  );
}
